import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Box,
  IconButton,
  CircularProgress,
  Collapse,
  Fade
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import DatePickerWrapper from '../DatePicker/DatePickerWrapper';
import { ko } from 'date-fns/locale';
import SearchIcon from '@mui/icons-material/Search'; 
import SyncAgGrid from '../common/DataGrid/SyncAgGrid';
import { axiosPrivate } from "../../api/axios";
import { useTranslation } from 'react-i18next';

const BatchAddMachineDialog = ({ 
  open, 
  onClose, 
  onSave, 
  machineGroups = [],
  machineCategories = [],
  group_code,
  project_code,
  usedDtSyncIds = []
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [dtMachines, setDtMachines] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const syncGridRef = useRef(null);
  const [selectedDtMachines, setSelectedDtMachines] = useState([]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (open) {
      const initialData = {
            machine_name: '',
            machine_manage_code: '',
            machine_category: machineCategories.length > 0 ? machineCategories[0].machine_category_id : '',
            machine_group: machineGroups.length > 0 ? machineGroups[0].machine_group_id : '',
            machine_manufacturer: '',
            machine_model_name: '',
            machine_purchase_date: new Date(),
            machine_size: '',
            machine_volt: ''
          };
        setFormData(initialData);
      setErrors({});
      setAlertMessage('');
      setShowSyncPanel(false);
      setSelectedDtMachines([]);
    }
  }, [open, machineGroups, machineCategories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (date, name) => {
    setFormData(prev => ({ ...prev, [name]: date }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
      }
  };

  const handleSave = () => {
    if (selectedDtMachines.length === 0) {
      setAlertMessage(t('pages.master.mch.batchDialog.messages.selectAtLeastOne'));
      return;
    }

    const batchData = selectedDtMachines.map(machine => ({
      ...formData,
      dt_sync_id: machine.mchId,
      machine_name: machine.mesMchId,
      machine_purchase_date: formatDate(formData.machine_purchase_date),
      // 빈 문자열은 NULL로 변환
      machine_category: formData.machine_category === '' ? null : formData.machine_category,
      machine_group: formData.machine_group === '' ? null : formData.machine_group,
    }));
    
    // onSave에 batchData와 'batch-add' 모드를 전달
    onSave(batchData, 'batch-add'); 
  };

  const loadDtMachines = async () => {
    if (!group_code || !project_code) return;
    
    setSyncLoading(true);
    try {
      const response = await axiosPrivate.post('/master/machines/dt-list', {
        group_code,
        project_code
      });
      
      const machinesWithId = response.data.map((machine, index) => ({
        ...machine,
        id: machine.mchId || `row-${index}`,
        isUsed: usedDtSyncIds.includes(machine.mchId)
      }));
      
      setDtMachines(machinesWithId);
    } catch (err) {
      console.error("디지털 트윈 장비 목록 조회 실패:", err);
      setAlertMessage(t('pages.master.mch.batchDialog.messages.dtLoadError'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDtSyncIdClick = () => {
    setShowSyncPanel(!showSyncPanel);
    if (!showSyncPanel) {
      loadDtMachines();
    }
  };

  const handleSyncSelectionChanged = () => {
    if (syncGridRef.current && syncGridRef.current.api) {
      const selectedNodes = syncGridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedDtMachines(selectedData);
    }
  };

  const syncColumnDefs = [
    { 
      headerName: '',
      checkboxSelection: params => !params.data.isUsed,
      width: 50,
      filter: false,
      sortable: false,
      resizable: false,
      cellClassRules: {
        'ag-disabled': params => params.data.isUsed,
      },
    },
    { field: 'mchId', headerName: t('pages.master.mch.batchDialog.syncGrid.columns.machineId'), flex: 1 },
    { field: 'mesMchId', headerName: t('pages.master.mch.batchDialog.syncGrid.columns.machineName'), flex: 2 },
    { 
      field: 'isUsed', 
      headerName: t('pages.master.mch.batchDialog.syncGrid.columns.status'), 
      flex: 1,
      cellRenderer: params => params.value ? t('pages.master.mch.batchDialog.syncGrid.status.inUse') : t('pages.master.mch.batchDialog.syncGrid.status.available'),
      cellStyle: params => params.value ? { color: 'red' } : { color: 'green' }
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
        {t('pages.master.mch.batchDialog.title')}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        {alertMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAlertMessage('')}>
            {alertMessage}
          </Alert>
        )}
    <>
      <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                  onClick={handleDtSyncIdClick}
                >
                {t('pages.master.mch.batchDialog.buttons.selectDtMachine')}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Collapse in={showSyncPanel} timeout={300}>
                <Fade in={showSyncPanel} timeout={400}>
                  <Box sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'background.paper', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      {t('pages.master.mch.batchDialog.syncPanel.title')}
                    </Typography>
                    <Box sx={{ height: 300, width: '100%' }}>
                      {syncLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <CircularProgress size={24} /> <Typography sx={{ ml: 1 }}>{t('pages.master.mch.batchDialog.messages.loading')}</Typography>
                        </Box>
                      ) : (
                        <SyncAgGrid
                          gridRef={syncGridRef}
                          rowData={dtMachines}
                          columnDefs={syncColumnDefs}
                          onSelectionChanged={handleSyncSelectionChanged}
                          isRowSelectable={(params) => !params.data.isUsed}
                          rowSelection="multiple"
                          suppressRowClickSelection={true}
                        />
                      )}
                    </Box>
                  </Box>
                </Fade>
              </Collapse>
            </Grid>
            
            <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                    {t('pages.master.mch.batchDialog.selectedCount', { count: selectedDtMachines.length })}
                </Typography>
        </Grid>

            <Grid item xs={12}> <Divider sx={{ my: 1 }} /> </Grid>

            <Grid item xs={12}>
          <TextField
            name="machine_name"
            label={t('pages.master.mch.batchDialog.form.machineName')}
            fullWidth
                value=""
                disabled
                helperText={t('pages.master.mch.batchDialog.form.machineNameHelper')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
              <TextField name="machine_manage_code" label={t('pages.master.mch.batchDialog.form.manageCode')} fullWidth value={formData.machine_manage_code || ''} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
              <TextField name="machine_manufacturer" label={t('pages.master.mch.batchDialog.form.manufacturer')} fullWidth value={formData.machine_manufacturer || ''} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
                <InputLabel>{t('pages.master.mch.batchDialog.form.machineCategory')}</InputLabel>
            <Select name="machine_category" value={formData.machine_category || ''} onChange={handleChange} label={t('pages.master.mch.batchDialog.form.machineCategory')}>
              {machineCategories.length === 0 ? (
                <MenuItem value="">생성된 장비 분류가 없습니다.</MenuItem>
              ) : (
                machineCategories.map(category => (
                  <MenuItem key={category.machine_category_id} value={category.machine_category_id}>{category.machine_category_name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
                <InputLabel>{t('pages.master.mch.batchDialog.form.machineGroup')}</InputLabel>
            <Select name="machine_group" value={formData.machine_group || ''} onChange={handleChange} label={t('pages.master.mch.batchDialog.form.machineGroup')}>
              {machineGroups.length === 0 ? (
                <MenuItem value="">생성된 장비 그룹이 없습니다.</MenuItem>
              ) : (
                machineGroups.map(group => (
                  <MenuItem key={group.machine_group_id} value={group.machine_group_id}>{group.machine_group_name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
              <TextField name="machine_model_name" label={t('pages.master.mch.batchDialog.form.modelName')} fullWidth value={formData.machine_model_name || ''} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePickerWrapper>
                <DatePicker selected={formData.machine_purchase_date} onChange={(date) => handleDateChange(date, 'machine_purchase_date')} dateFormat="yyyy-MM-dd" locale={ko}
                  customInput={<TextField fullWidth label={t('pages.master.mch.batchDialog.form.purchaseDate')} />} />
          </DatePickerWrapper>
        </Grid>
        <Grid item xs={12} sm={6}>
              <TextField name="machine_size" label={t('pages.master.mch.batchDialog.form.size')} fullWidth value={formData.machine_size || ''} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
              <TextField name="machine_volt" label={t('pages.master.mch.batchDialog.form.volt')} fullWidth value={formData.machine_volt || ''} onChange={handleChange} />
        </Grid>
      </Grid>
        </>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('pages.master.mch.batchDialog.buttons.save')}
        </Button>
        <Button onClick={onClose} color="inherit">
          {t('pages.master.mch.batchDialog.buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchAddMachineDialog; 